import { EventSubscriber, hashObject, } from '@verdant-web/common';
import { Metadata } from '../metadata/Metadata.js';
import { openMetadataDatabase, openWIPMetadataDatabase, } from '../metadata/openMetadataDatabase.js';
import { openDocumentDatabase, openWIPDocumentDatabase, } from '../migration/openDatabase.js';
import { UndoHistory } from '../UndoHistory.js';
import { Client } from './Client.js';
import { deleteAllDatabases, deleteDatabase, getAllDatabaseNamesAndVersions, } from '../idb.js';
import { FakeWeakRef } from '../FakeWeakRef.js';
import { METADATA_VERSION_KEY } from './constants.js';
/**
 * Since storage initialization is async, this class wraps the core
 * Storage creation promise and exposes some metadata which can
 * be useful immediately.
 */
export class ClientDescriptor {
    get namespace() {
        return this._namespace;
    }
    constructor(init) {
        this.init = init;
        this._initializing = false;
        this.initialize = async (init) => {
            // if server-side and no alternative IndexedDB implementation was provided,
            // we can't initialize the storage
            if (typeof window === 'undefined' && !init.indexedDb) {
                throw new Error('A verdant client was initialized in an environment without IndexedDB. If you are using verdant in a server-rendered framework, you must enforce that all clients are initialized on the client-side, or you must provide some mock interface of IDBFactory to the ClientDescriptor options.');
            }
            if (this._initializing || this._resolvedValue) {
                return this._readyPromise;
            }
            this._initializing = true;
            try {
                let storage;
                if (init.schema.wip) {
                    storage = await this.initializeWIPDatabases(init);
                }
                else {
                    storage = await this.initializeDatabases(init);
                    this.cleanupWIPDatabases(init);
                }
                this.resolveReady(storage);
                this._resolvedValue = storage;
                return storage;
            }
            catch (err) {
                if (err instanceof Error) {
                    this.rejectReady(err);
                    throw err;
                }
                else {
                    throw new Error('Unknown error initializing storage');
                }
            }
            finally {
                this._initializing = false;
            }
        };
        this.initializeDatabases = async (init) => {
            const metadataVersion = init[METADATA_VERSION_KEY];
            const { db: metaDb } = await openMetadataDatabase({
                indexedDB: init.indexedDb,
                log: init.log,
                namespace: init.namespace,
                metadataVersion,
            });
            const context = {
                namespace: this._namespace,
                metaDb,
                schema: init.schema,
                log: init.log || (() => { }),
                undoHistory: init.undoHistory || new UndoHistory(),
                entityEvents: new EventSubscriber(),
                globalEvents: new EventSubscriber(),
                internalEvents: new EventSubscriber(),
                weakRef: (value) => {
                    if (init.EXPERIMENTAL_weakRefs) {
                        return new WeakRef(value);
                    }
                    else {
                        return new FakeWeakRef(value);
                    }
                },
                migrations: init.migrations,
            };
            const meta = new Metadata({
                context,
                disableRebasing: init.disableRebasing,
            });
            // verify schema integrity
            await meta.updateSchema(init.schema, init.overrideSchemaConflict);
            const contextWithNow = Object.assign(context, {
                getNow: () => meta.now,
            });
            const documentDb = await openDocumentDatabase({
                context: contextWithNow,
                version: init.schema.version,
                meta,
                migrations: init.migrations,
                indexedDB: init.indexedDb,
            });
            const fullContext = Object.assign(contextWithNow, { documentDb });
            const storage = new Client({
                syncConfig: init.sync,
                migrations: init.migrations,
                files: init.files,
            }, fullContext, {
                meta,
            });
            return storage;
        };
        this.initializeWIPDatabases = async (init) => {
            const schemaHash = hashObject(init.schema);
            console.info(`WIP schema in use. Opening database with hash ${schemaHash}`);
            const wipNamespace = `@@wip_${init.namespace}_${schemaHash}`;
            const { db: metaDb } = await openWIPMetadataDatabase({
                indexedDB: init.indexedDb,
                log: init.log,
                namespace: init.namespace,
                wipNamespace: wipNamespace,
            });
            const context = {
                namespace: this._namespace,
                metaDb,
                schema: init.schema,
                log: init.log || (() => { }),
                undoHistory: init.undoHistory || new UndoHistory(),
                entityEvents: new EventSubscriber(),
                globalEvents: new EventSubscriber(),
                internalEvents: new EventSubscriber(),
                weakRef: (value) => {
                    if (init.EXPERIMENTAL_weakRefs) {
                        return new WeakRef(value);
                    }
                    else {
                        return new FakeWeakRef(value);
                    }
                },
                migrations: init.migrations,
            };
            const meta = new Metadata({
                context,
                disableRebasing: init.disableRebasing,
            });
            const contextWithNow = Object.assign(context, {
                getNow: () => meta.now,
            });
            // verify schema integrity
            await meta.updateSchema(init.schema, init.overrideSchemaConflict);
            const documentDb = await openWIPDocumentDatabase({
                context: contextWithNow,
                version: init.schema.version,
                meta,
                migrations: init.migrations,
                indexedDB: init.indexedDb,
                wipNamespace,
            });
            const fullContext = Object.assign(contextWithNow, { documentDb });
            const storage = new Client({
                syncConfig: init.sync,
                migrations: init.migrations,
                files: init.files,
            }, fullContext, {
                meta,
            });
            return storage;
        };
        this.cleanupWIPDatabases = async (init) => {
            const databaseInfo = await getAllDatabaseNamesAndVersions(init.indexedDb);
            const wipDatabases = databaseInfo
                .filter((db) => { var _a; return (_a = db.name) === null || _a === void 0 ? void 0 : _a.startsWith('@@wip_'); })
                .map((db) => db.name);
            // don't clear a current WIP database.
            const wipDatabasesToDelete = wipDatabases.filter((db) => !db.startsWith(`@@wip_${init.namespace}_${hashObject(init.schema)}`));
            for (const db of wipDatabasesToDelete) {
                await deleteDatabase(db, init.indexedDb);
            }
        };
        this.open = () => this.initialize(this.init);
        this.close = async () => {
            if (this._resolvedValue) {
                this._resolvedValue.close();
            }
            if (this._initializing) {
                (await this._readyPromise).close();
            }
        };
        this.__dangerous__resetLocal = async () => {
            await deleteAllDatabases(this.namespace);
        };
        this._readyPromise = new Promise((resolve, reject) => {
            this.resolveReady = resolve;
            this.rejectReady = reject;
        });
        this._namespace = init.namespace;
    }
    get current() {
        // exposing an immediate value if already resolved lets us
        // skip the promise microtask when accessing this externally if
        // the initialization has been completed.
        return this._resolvedValue;
    }
    get readyPromise() {
        return this._readyPromise;
    }
    get schema() {
        return this.init.schema;
    }
}
//# sourceMappingURL=ClientDescriptor.js.map